'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useCallback, useMemo } from 'react';
import { TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import {
  buildSaleBundle,
  getSaleController,
  resolveSaleLabel,
} from '../facetControllers';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { SaleFacetProps } from './SaleFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

const SALE_SEED_VALUES: StringFlatFacetValue[] = [
  { value: 'flash_deal', state: 'idle', numberOfResults: 0 },
  { value: 'on_sale', state: 'idle', numberOfResults: 0 },
  { value: 'new_release_discount', state: 'idle', numberOfResults: 0 },
];

export const SaleFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: SaleFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const saleTitle =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filter-onsale-label')
      ?.value ?? 'Sale';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const saleBundle = useMemo(
    () => buildSaleBundle(searchValuesMicrocopy),
    [searchValuesMicrocopy]
  );

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getSaleController,
    fieldName: 'ec_sale_filters',
    getValueId,
    seedValues: SALE_SEED_VALUES,
  });

  const getLabel = useCallback(
    (value: string) => resolveSaleLabel(saleBundle, value),
    [saleBundle]
  );

  const treeItems = useMemo(
    () =>
      optimisticValues.map((v) => ({
        id: getValueId(v),
        label: getLabel(v.value),
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [optimisticValues, getLabel]
  );

  const hasVisibleValues = optimisticValues.some(
    (v) => v.numberOfResults > 0 || v.state === 'selected'
  );
  if (!hasVisibleValues) return null;

  const isDark = variant === 'mobile';

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={saleTitle}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={saleTitle}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
